import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { GameTableForm } from '@/types';
import { ColumnDef } from '@tanstack/react-table';
import { X } from 'lucide-react';
import { useState } from 'react';

export const tableSettingColumns: ColumnDef<GameTableForm>[] = [
  {
    accessorKey: 'tableName',
    header: 'Table Name',
  },
  {
    accessorKey: 'gameSpeed',
    header: 'Game Speed',
    cell: ({ row }) => <span>{row.original.gameSpeed}s</span>,
  },
  {
    accessorKey: 'minBet',
    header: 'Min Bet',
    cell: ({ row }) => <span>₹{row.original.minBetAmount}</span>,
  },
  {
    accessorKey: 'maxBet',
    header: 'Max Bet',
    cell: ({ row }) => <span>₹{row.original.maxBetAmount}</span>,
  },
  {
    accessorKey: 'commission',
    header: 'Commission %',
    cell: ({ row }) => <span>{row.original.commission}%</span>,
  },
  {
    accessorKey: 'autoShuffle',
    header: 'Auto Shuffle',
    cell: ({ row }) =>
      row.original.autoShuffle ? (
        <span className="text-green-500 font-medium">On</span>
      ) : (
        <span className="text-red-500 font-medium">Off</span>
      ),
  },
  {
    accessorKey: 'resultMode',
    header: 'Result Mode',
    cell: ({ row }) => {
      const mode = row.original.resultMode;

      return (
        <span className={mode === 'random' ? 'text-blue-400' : 'text-yellow-400 font-semibold'}>
          {mode}
        </span>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const active = row.original.status === 'Active';
      return (
        <span className={active ? 'text-green-500 font-semibold' : 'text-red-500 font-semibold'}>
          {active ? 'Active' : 'Stopped'}
        </span>
      );
    },
  },
  {
    accessorKey: 'actions',
    header: 'Actions',
    cell: () => {
      return (
        <div className="flex gap-2">
          <button className="text-blue-500">Edit</button>
          <button className="text-yellow-500">Save</button>
        </div>
      );
    },
  },
];

export function TableEdit() {
  const [isOpen, setIsOpen] = useState(false);
  const createNewTable = () => {
    setIsOpen(true);
  };

  const cancelCreate = () => {
    setIsOpen(false);
  };
  return (
    <div className="w-full h-screen">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Configure Game Table</h2>
          <p className="text-muted-foreground">
            Set up or modify table settings including bet limits, round duration, commission rate,
            shuffle mode, and result control. Used for customizing game flow and betting experience.
          </p>
        </div>
      </div>
      {isOpen && (
        <div className="relative w-full rounded-xl border border-muted-foreground p-10 mb-4 bg-background shadow-lg">
          <Button
            className="absolute top-4 right-4"
            variant="destructive"
            size="sm"
            onClick={cancelCreate}
          >
            <X className="w-4 h-4" />
          </Button>

          {/* Section Title */}
          <h2 className="text-xl font-semibold mb-6">Create Game Table</h2>

          {/* FORM GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Table Name */}
            <div className="space-y-2">
              <Label>Table Name</Label>
              <Input placeholder="Enter table name" />
            </div>

            {/* Game Speed */}
            <div className="space-y-2">
              <Label>Game Speed</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select speed" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Speed (seconds)</SelectLabel>
                    <SelectItem value="12">12s</SelectItem>
                    <SelectItem value="15">15s</SelectItem>
                    <SelectItem value="20">20s</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Min Bet */}
            <div className="space-y-2">
              <Label>Min Bet Amount (₹)</Label>
              <Input type="number" placeholder="Min bet amount" />
            </div>

            {/* Max Bet */}
            <div className="space-y-2">
              <Label>Max Bet Amount (₹)</Label>
              <Input type="number" placeholder="Max bet amount" />
            </div>

            {/* Commission */}
            <div className="space-y-2">
              <Label>Commission % (optional)</Label>
              <Input type="number" placeholder="0 - 100" />
            </div>

            {/* Result Mode */}
            <div className="space-y-2">
              <Label>Result Mode</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Modes</SelectLabel>
                    <SelectItem value="random">Random</SelectItem>
                    <SelectItem value="manual">Manual (Admin)</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Auto Shuffle */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <Label className="text-sm">Auto Shuffle</Label>
              <Switch />
            </div>

            {/* Status */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <Label className="text-sm">Status</Label>
              <Select>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Active" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Stopped">Stopped</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 mt-10">
            <Button variant="outline" onClick={cancelCreate}>
              Cancel
            </Button>
            <Button className="bg-primary text-primary-foreground">Create Table</Button>
          </div>
        </div>
      )}
      <DataTable
        columns={tableSettingColumns}
        data={[]}
        enableCreateTable={true}
        onCreate={createNewTable}
        pageIndex={0}
        total={0}
        onPageChange={function (page: number): void {
          throw new Error('Function not implemented.');
        }}
        onPageSizeChange={function (size: number): void {
          throw new Error('Function not implemented.');
        }}
      />
    </div>
  );
}
